import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TenantWindow } from '../../../../api/organization/allocationWindowTypes';
import { WEEKDAY_KEYS } from '../../../organizations/allocationWindows/GrantWindowsEditor';
import { hourRange, minutesOf, placeWindows } from './seasonPlan';

const HOUR_PX = 28;

/**
 * One room's season plan (M11, M4 spec §6): Monday to Sunday, the windows
 * as blocks with the organisation's name. In the tenant primary only, as an
 * outline on a light tint (one accent colour, design-decisions); the
 * organisation is told apart by its name, not by a colour.
 */
const SeasonPlanGrid: React.FC<{ windows: TenantWindow[] }> = ({ windows }) => {
    const { t } = useTranslation();
    const { from, to } = hourRange(windows);
    const placed = placeWindows(windows);
    const hours = Array.from({ length: to - from }, (_, i) => from + i);
    const height = (to - from) * HOUR_PX;

    return (
        <div className={'overflow-x-auto'}>
            <div className={'grid min-w-[640px]'} style={{ gridTemplateColumns: '3rem repeat(7, minmax(0, 1fr))' }}>
                <div />
                {[1, 2, 3, 4, 5, 6, 7].map((weekday) => (
                    <div key={weekday} className={'text-xs font-medium text-foreground/60 px-1 pb-1'}>
                        {t(`asset_detail.weekdays.${WEEKDAY_KEYS[weekday]}`)}
                    </div>
                ))}
                <div className={'relative'} style={{ height }}>
                    {hours.map((hour) => (
                        <div
                            key={hour}
                            className={'absolute right-1 text-[10px] text-foreground/60'}
                            style={{ top: (hour - from) * HOUR_PX - 6 }}
                        >
                            {`${String(hour).padStart(2, '0')}:00`}
                        </div>
                    ))}
                </div>
                {[1, 2, 3, 4, 5, 6, 7].map((weekday) => (
                    <div key={weekday} className={'relative border-l border-foreground/5'} style={{ height }}>
                        {hours.map((hour) => (
                            <div
                                key={hour}
                                className={'absolute inset-x-0 border-t border-foreground/5'}
                                style={{ top: (hour - from) * HOUR_PX }}
                            />
                        ))}
                        {placed.filter((p) => p.window.weekday === weekday).map(({ window, lane, lanes }) => {
                            const top = ((minutesOf(window.startTime) - from * 60) / 60) * HOUR_PX;
                            const blockHeight = ((minutesOf(window.endTime) - minutesOf(window.startTime)) / 60) * HOUR_PX;
                            return (
                                <div
                                    key={window.windowId}
                                    className={'absolute rounded-[3px] border border-primary bg-primary/10 px-1 overflow-hidden'}
                                    style={{
                                        top,
                                        height: blockHeight,
                                        left: `calc(${(lane / lanes) * 100}% + 2px)`,
                                        width: `calc(${100 / lanes}% - 4px)`,
                                    }}
                                    title={t('allocation_windows.plan.block', {
                                        name: window.organizationName || t('common.not_applicable'),
                                        start: window.startTime,
                                        end: window.endTime,
                                    })}
                                >
                                    <p className={'text-[11px] font-medium text-foreground leading-tight truncate'}>
                                        {window.organizationName || t('common.not_applicable')}
                                    </p>
                                    <p className={'text-[10px] text-foreground/60 leading-tight'}>
                                        {`${window.startTime}–${window.endTime}`}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SeasonPlanGrid;
